import {
  NormalizedAlert,
  USGSFeature,
  USGSResponse,
  AlertResponseMetadata,
  AlertSeverity,
} from '../types/alert';
import { AlertCache } from './alertCache';

export class USGSAdapter {
  private static endpoint = 'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/2.5_day.geojson';

  /**
   * Determine severity deterministically based on Richter / Moment Magnitude
   */
  static deriveMagnitudeSeverity(mag: number): AlertSeverity {
    if (mag >= 6.0) return 'Extreme';
    if (mag >= 5.0) return 'Severe';
    if (mag >= 3.8) return 'Moderate';
    return 'Minor';
  }

  /**
   * Normalize incoming USGS Earthquake GeoJSON feature into NormalizedAlert
   */
  static normalizeEarthquake(feature: USGSFeature): NormalizedAlert | null {
    if (!feature || !feature.properties || !feature.geometry) return null;

    const p = feature.properties;
    const coords = feature.geometry.coordinates; // [lon, lat, depth]
    if (!Array.isArray(coords) || coords.length < 2) return null;

    const lon = coords[0];
    const lat = coords[1];
    const depthKm = coords[2] || 0;
    const mag = p.mag || 0;

    const severity = this.deriveMagnitudeSeverity(mag);
    const eventTime = new Date(p.time).toISOString();
    // Earthquakes remain actively notable for 24 hours
    const expiryTime = new Date(p.time + 24 * 60 * 60 * 1000).toISOString();

    const instruction =
      mag >= 4.5
        ? 'USGS Safety Advisory: DROP, COVER, AND HOLD ON. Expect aftershocks. Stay away from damaged masonry, windows, and electrical wires.'
        : 'USGS Informational: Minor seismic event. No immediate structural danger anticipated. Report felt reports to the USGS Did You Feel It? portal.';

    let country: import('../types/alert').CountryCode = 'GLOBAL';
    const placeLower = (p.place || '').toLowerCase();
    if (
      placeLower.includes('alaska') ||
      placeLower.includes('california') ||
      placeLower.includes('hawaii') ||
      placeLower.includes('oklahoma') ||
      placeLower.includes('texas') ||
      placeLower.includes('washington') ||
      placeLower.includes('puerto rico') ||
      placeLower.includes('nevada') ||
      placeLower.includes('idaho') ||
      placeLower.includes('montana') ||
      placeLower.includes('utah') ||
      placeLower.includes('wyoming') ||
      placeLower.includes(', ca') ||
      placeLower.includes(', ak') ||
      placeLower.includes(', hi') ||
      placeLower.includes(', nv') ||
      placeLower.includes(', or') ||
      placeLower.includes(', wa') ||
      placeLower.includes(', tx') ||
      placeLower.includes(', ok')
    ) {
      country = 'US';
    } else if (
      placeLower.includes('canada') ||
      placeLower.includes('british columbia') ||
      placeLower.includes('alberta') ||
      placeLower.includes('yukon') ||
      placeLower.includes('quebec') ||
      placeLower.includes('ontario')
    ) {
      country = 'CA';
    } else if (
      placeLower.includes('australia') ||
      placeLower.includes('new south wales') ||
      placeLower.includes('victoria') ||
      placeLower.includes('queensland')
    ) {
      country = 'AU';
    } else if (
      placeLower.includes('united kingdom') ||
      placeLower.includes('england') ||
      placeLower.includes('scotland')
    ) {
      country = 'GB';
    }

    return {
      id: `usgs_${feature.id}`,
      event: `M ${mag.toFixed(1)} Earthquake`,
      headline: p.title || `Magnitude ${mag.toFixed(1)} Earthquake detected near ${p.place}`,
      description: `Seismic event detected by the USGS National Earthquake Information Center. Depth: ${depthKm.toFixed(1)} km. Tsunami risk flag: ${p.tsunami ? 'WARNING/WATCH' : 'None reported'}. Total felt reports submitted: ${p.felt ?? 0}.`,
      instruction,
      severity,
      urgency: mag >= 5.0 ? 'Immediate' : 'Past',
      certainty: 'Observed',
      senderName: 'USGS National Earthquake Information Center',
      areaDesc: p.place || 'Location coordinates on file',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
      centroid: [lat, lon],
      effective: eventTime,
      onset: eventTime,
      expires: expiryTime,
      ends: null,
      status: 'Actual',
      messageType: 'Alert',
      category: 'Geo',
      disruptionType: 'Earthquake',
      sourceAgency: 'USGS',
      country,
      magnitude: mag,
      depthKm,
      sourceUrl: p.url || `https://earthquake.usgs.gov/earthquakes/eventpage/${feature.id}`,
      firstSeen: eventTime,
      lastSeen: new Date().toISOString(),
    };
  }

  /**
   * Fetch live M2.5+ earthquakes from USGS
   */
  static async fetchEarthquakes(options?: {
    forceRefresh?: boolean;
  }): Promise<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }> {
    const cacheKey = 'usgs_earthquakes_2_5_day';

    if (!options?.forceRefresh) {
      const cached = AlertCache.get<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }>(cacheKey);
      if (cached && !cached.isStale) {
        return {
          alerts: cached.data.alerts,
          metadata: {
            ...cached.data.metadata,
            isCached: true,
            cacheAgeSeconds: cached.ageSeconds,
          },
        };
      }
    }

    try {
      const res = await fetch(this.endpoint, {
        headers: {
          'Accept': 'application/json, application/geo+json',
        },
      });

      if (!res.ok) {
        throw new Error(`USGS HTTP ${res.status}: ${res.statusText}`);
      }

      const json: USGSResponse = await res.json();
      if (!json || !Array.isArray(json.features)) {
        throw new Error('USGS response malformed');
      }

      const normalized: NormalizedAlert[] = [];
      for (const feat of json.features) {
        const item = this.normalizeEarthquake(feat);
        if (item) normalized.push(item);
      }

      const metadata: AlertResponseMetadata = {
        source: 'US Geological Survey (USGS)',
        totalAlerts: normalized.length,
        lastChecked: new Date().toISOString(),
        isCached: false,
        cacheAgeSeconds: 0,
        statusMessage: 'Verified live response from USGS Earthquake Hazards Program',
      };

      AlertCache.set(cacheKey, { alerts: normalized, metadata });

      return { alerts: normalized, metadata };
    } catch (err: any) {
      const fallback = AlertCache.get<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }>(cacheKey);
      if (fallback) {
        return {
          alerts: fallback.data.alerts.map(a => ({ ...a, isStale: true })),
          metadata: {
            ...fallback.data.metadata,
            isCached: true,
            isFallback: true,
            cacheAgeSeconds: fallback.ageSeconds,
            statusMessage: `USGS warning: ${err.message}. Showing cached data.`,
          },
        };
      }
      throw new Error(`Failed to retrieve USGS earthquake data: ${err.message}`);
    }
  }
}
